/**
 * Plans — pick the plans the agents work on.
 *
 * Select any set of plans and run the rules builder on just those (Force
 * re-drafts sources whose text has not changed), have the source finder search
 * the web for their documents, or run the harness on them.
 * Each row opens to its sources. Plans not yet in the payer table can be added
 * from the national payer directory. The harness's Fix lands here, opened on
 * the plan (and source) that needs one.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Play, Plus, RefreshCw, Search, ShieldCheck } from 'lucide-react';
import { funderAgentsApi, type AgentName, type DirectoryPayer, type FunderPlan } from '../../../shared/services/funderAgentsApi';
import SourcesEditor from './SourcesEditor';
import { Btn, Empty, ErrorLine, errText, fmtDay, type Notify } from './shared';

const PAGE = 50;

/** Where the harness sent the person: a plan's sources, optionally one source. */
export interface SourcesFocus {
  payerId: number;
  payerName: string;
  sourceId?: number | null;
  message: string;
}

export default function PlansView({
  notify,
  onRunStarted,
  onOpenRules,
  focus,
}: {
  notify: Notify;
  onRunStarted: () => void;
  onOpenRules: (plan: FunderPlan) => void;
  focus?: SourcesFocus | null;
}) {
  const [q, setQ] = useState('');
  const [state, setState] = useState('');
  const [hasSources, setHasSources] = useState(false);
  const [hasFindings, setHasFindings] = useState(false);
  const [offset, setOffset] = useState(0);
  const [plans, setPlans] = useState<FunderPlan[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [expanded, setExpanded] = useState<number | null>(null);
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const [dirOpen, setDirOpen] = useState(false);
  const [dirQ, setDirQ] = useState('');
  const [dirState, setDirState] = useState('');
  const [dirResults, setDirResults] = useState<DirectoryPayer[] | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await funderAgentsApi.plans({
        q: q.trim() || undefined,
        state: state.trim().toUpperCase() || undefined,
        has_sources: hasSources,
        has_findings: hasFindings,
        limit: PAGE,
        offset,
      });
      setPlans(res.plans);
      setTotal(res.total);
    } catch (e) {
      setError(errText(e));
      setPlans([]);
    }
  }, [q, state, hasSources, hasFindings, offset]);

  useEffect(() => {
    const t = window.setTimeout(() => void load(), 250);
    return () => window.clearTimeout(t);
  }, [load]);

  // Opened from a finding: narrow to that plan and open its sources.
  useEffect(() => {
    if (!focus) return;
    setQ(focus.payerName);
    setState('');
    setHasSources(false);
    setHasFindings(false);
    setOffset(0);
    setExpanded(focus.payerId);
  }, [focus]);

  const allOnPage = useMemo(() => (plans ?? []).map((p) => p.payer_id), [plans]);
  const pageAllSelected = allOnPage.length > 0 && allOnPage.every((id) => selected.has(id));
  const toggle = (id: number) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const start = async (agent: AgentName) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const what =
      agent === 'builder'
        ? `Draft rules for ${ids.length} plan${ids.length === 1 ? '' : 's'}${force ? ', re-drafting unchanged sources (Force)' : ''}?\n\nThe agent reads each enabled source and leaves proposals for a person to approve. Nothing is applied.`
        : agent === 'finder'
          ? `Search the web for source documents for ${ids.length} plan${ids.length === 1 ? '' : 's'}?\n\nPlans that already have a source or a waiting suggestion are skipped. What it finds waits in Sources found for a person to accept.`
          : `Check ${ids.length} plan${ids.length === 1 ? '' : 's'} for conflicts, invalid values, stale sources and denial patterns?\n\nThe harness opens findings; it never changes a rule.`;
    if (!window.confirm(what)) return;
    setBusy(agent);
    try {
      const run = await funderAgentsApi.startRun({ agent, payer_ids: ids, force: agent === 'builder' ? force : undefined });
      const name = agent === 'builder' ? 'Rules builder' : agent === 'finder' ? 'Source finder' : 'Harness';
      notify(true, `${name} run #${run?.id ?? ''} queued for ${ids.length} plan${ids.length === 1 ? '' : 's'}.`);
      onRunStarted();
    } catch (e) {
      notify(false, errText(e));
    } finally {
      setBusy(null);
    }
  };

  const searchDir = async () => {
    setBusy('dir');
    try {
      setDirResults(await funderAgentsApi.searchDirectory(dirQ.trim(), dirState.trim().toUpperCase() || undefined));
    } catch (e) {
      notify(false, errText(e));
      setDirResults([]);
    } finally {
      setBusy(null);
    }
  };

  const link = async (d: DirectoryPayer) => {
    setBusy(`l${d.id}`);
    try {
      const plan = await funderAgentsApi.linkPlan(d.id);
      notify(true, `${d.display_name} added. Add a source URL, then draft rules.`);
      setDirResults((r) => (r ?? []).map((x) => (x.id === d.id ? { ...x, payer_id: plan?.payer_id ?? x.payer_id ?? -1 } : x)));
      await load();
      if (plan?.payer_id) setExpanded(plan.payer_id);
    } catch (e) {
      notify(false, errText(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs font-semibold text-ink-700 flex-1 min-w-[200px]">
          Search plans
          <span className="mt-1 flex items-center gap-1 rounded-md border border-v2border bg-surface px-2">
            <Search className="w-3.5 h-3.5 text-ink-400" aria-hidden="true" />
            <input
              value={q}
              onChange={(e) => {
                setOffset(0);
                setQ(e.target.value);
              }}
              placeholder="Name or payer ID"
              className="w-full bg-transparent py-1.5 text-sm text-ink outline-none"
            />
          </span>
        </label>
        <label className="text-xs font-semibold text-ink-700">
          State
          <input
            value={state}
            onChange={(e) => {
              setOffset(0);
              setState(e.target.value);
            }}
            maxLength={2}
            placeholder="CA"
            className="mt-1 block w-16 rounded-md border border-v2border bg-surface px-2 py-1.5 text-sm uppercase text-ink"
          />
        </label>
        <label className="inline-flex items-center gap-1 text-xs text-ink-700 pb-2">
          <input type="checkbox" checked={hasSources} onChange={(e) => { setOffset(0); setHasSources(e.target.checked); }} />
          Has sources
        </label>
        <label className="inline-flex items-center gap-1 text-xs text-ink-700 pb-2">
          <input type="checkbox" checked={hasFindings} onChange={(e) => { setOffset(0); setHasFindings(e.target.checked); }} />
          Has open findings
        </label>
        <Btn tone="ghost" title="Refresh" onClick={() => void load()}>
          <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" />
        </Btn>
        <Btn onClick={() => setDirOpen((o) => !o)}>
          <Plus className="w-3.5 h-3.5" aria-hidden="true" />
          Add a plan from the national directory
        </Btn>
      </div>

      {dirOpen && (
        <div className="rounded-md border border-v2border bg-paper p-3 space-y-2">
          <div className="flex flex-wrap items-end gap-2">
            <input
              value={dirQ}
              onChange={(e) => setDirQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void searchDir()}
              placeholder="Search Medicaid payers by name"
              className="flex-1 min-w-[200px] rounded-md border border-v2border bg-surface px-2 py-1.5 text-sm text-ink"
            />
            <input
              value={dirState}
              onChange={(e) => setDirState(e.target.value)}
              maxLength={2}
              placeholder="State"
              className="w-20 rounded-md border border-v2border bg-surface px-2 py-1.5 text-sm uppercase text-ink"
            />
            <Btn tone="primary" busy={busy === 'dir'} disabled={!dirQ.trim() && !dirState.trim()} onClick={() => void searchDir()}>
              Search
            </Btn>
          </div>
          {dirResults !== null &&
            (dirResults.length === 0 ? (
              <p className="text-sm text-ink-500">No Medicaid payers match. Try a shorter name or another state.</p>
            ) : (
              <ul className="divide-y divide-v2border rounded-md border border-v2border bg-surface">
                {dirResults.map((d) => (
                  <li key={d.id} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
                    <span className="font-semibold text-ink">{d.display_name}</span>
                    <span className="text-xs text-ink-500">
                      {d.primary_payer_id ? `Payer ID ${d.primary_payer_id}` : 'No payer ID'}
                      {d.operating_states?.length ? ` · ${d.operating_states.slice(0, 6).join(', ')}` : ''}
                    </span>
                    <span className="ml-auto">
                      {d.payer_id ? (
                        <span className="text-xs text-ink-500">Already a plan</span>
                      ) : (
                        <Btn busy={busy === `l${d.id}`} onClick={() => void link(d)}>
                          Add as plan
                        </Btn>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            ))}
        </div>
      )}

      {focus && expanded === focus.payerId && (
        <p className="rounded-md bg-attention-soft px-3 py-2 text-sm text-attention-ink">
          {focus.payerName}: {focus.message}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3 rounded-md border border-v2border bg-paper-2 px-3 py-2">
        <span className="text-sm text-ink-700">
          {selected.size === 0 ? 'Select plans to run the agents on them.' : `${selected.size} plan${selected.size === 1 ? '' : 's'} selected`}
        </span>
        {selected.size > 0 && (
          <Btn tone="ghost" onClick={() => setSelected(new Set())}>
            Clear
          </Btn>
        )}
        <span className="ml-auto flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-1 text-xs text-ink-700" title="Re-draft even when a source's text has not changed">
            <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
            Force
          </label>
          <Btn tone="primary" disabled={selected.size === 0} busy={busy === 'builder'} onClick={() => void start('builder')}>
            <Play className="w-3.5 h-3.5" aria-hidden="true" />
            Draft rules for selected
          </Btn>
          <Btn disabled={selected.size === 0} busy={busy === 'finder'} onClick={() => void start('finder')}>
            <Search className="w-3.5 h-3.5" aria-hidden="true" />
            Find sources for selected
          </Btn>
          <Btn disabled={selected.size === 0} busy={busy === 'harness'} onClick={() => void start('harness')}>
            <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" />
            Check selected
          </Btn>
        </span>
      </div>

      <ErrorLine text={error} />
      {plans === null ? (
        <p className="text-sm text-ink-500">Loading plans…</p>
      ) : plans.length === 0 ? (
        <Empty>No plans match. Clear the filters, or add a plan from the national directory.</Empty>
      ) : (
        <div className="overflow-x-auto rounded-md border border-v2border bg-surface">
          <table className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className="border-b border-v2border text-left text-xs text-ink-500">
                <th className="p-2 w-8">
                  <input
                    type="checkbox"
                    aria-label="Select all plans on this page"
                    checked={pageAllSelected}
                    onChange={() =>
                      setSelected((s) => {
                        const n = new Set(s);
                        allOnPage.forEach((id) => (pageAllSelected ? n.delete(id) : n.add(id)));
                        return n;
                      })
                    }
                  />
                </th>
                <th className="p-2">Plan</th>
                <th className="p-2">States</th>
                <th className="p-2" title="generic · state · its own">Rules</th>
                <th className="p-2">Sources</th>
                <th className="p-2">Awaiting approval</th>
                <th className="p-2">Open findings</th>
                <th className="p-2">Last drafted</th>
                <th className="p-2" />
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => {
                const open = expanded === p.payer_id;
                return (
                  <React.Fragment key={p.payer_id}>
                    <tr className="border-b border-v2border last:border-0 hover:bg-paper">
                      <td className="p-2">
                        <input type="checkbox" aria-label={`Select ${p.payer_name}`} checked={selected.has(p.payer_id)} onChange={() => toggle(p.payer_id)} />
                      </td>
                      <td className="p-2">
                        <button type="button" onClick={() => setExpanded(open ? null : p.payer_id)} className="inline-flex items-center gap-1 font-semibold text-ink text-left">
                          {open ? <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" /> : <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />}
                          {p.payer_name}
                        </button>
                        {p.edi_payer_id && <div className="text-[11px] text-ink-500 pl-5">Payer ID {p.edi_payer_id}</div>}
                      </td>
                      <td className="p-2 text-ink-700">{(p.states ?? []).slice(0, 4).join(', ') || '—'}</td>
                      <td className="p-2 tabular-nums text-ink-700">
                        {p.rules.generic} · {p.rules.statewide} · <span className="font-semibold text-ink">{p.rules.plan_specific}</span>
                      </td>
                      <td className="p-2 tabular-nums text-ink-700">
                        {p.sources_enabled}/{p.sources}
                        {p.suggestions_pending ? (
                          <span className="ml-1.5 rounded-full bg-v2accent-soft px-1.5 text-xs text-v2accent-ink" title="Found by the source finder, waiting in Sources found">
                            +{p.suggestions_pending} found
                          </span>
                        ) : null}
                      </td>
                      <td className="p-2 tabular-nums">{p.proposals_pending || '—'}</td>
                      <td className={`p-2 tabular-nums ${p.findings_open ? 'font-semibold text-danger-ink' : ''}`}>{p.findings_open || '—'}</td>
                      <td className="p-2 text-ink-500">{fmtDay(p.last_drafted_at)}</td>
                      <td className="p-2 text-right">
                        <Btn tone="ghost" onClick={() => onOpenRules(p)}>
                          Rules
                        </Btn>
                      </td>
                    </tr>
                    {open && (
                      <tr className="border-b border-v2border bg-paper">
                        <td />
                        <td colSpan={8} className="p-3">
                          <p className="text-xs font-semibold uppercase tracking-wider text-ink-500 mb-2">Sources the builder reads</p>
                          <SourcesEditor
                            payerId={p.payer_id}
                            notify={notify}
                            onChanged={() => void load()}
                            focusSourceId={focus?.payerId === p.payer_id ? focus.sourceId : null}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {total > PAGE && (
        <div className="flex items-center gap-2 text-sm text-ink-500">
          <Btn disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
            Previous
          </Btn>
          <span>
            {offset + 1}–{Math.min(offset + PAGE, total)} of {total}
          </span>
          <Btn disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>
            Next
          </Btn>
        </div>
      )}
    </div>
  );
}
